import React, { useState, useMemo, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  X,
  History,
  TrendingUp,
  TrendingDown,
  Filter,
  CheckCircle2,
  XCircle,
  Clock,
  Award,
  Flame,
  ArrowUpRight,
  ArrowDownRight,
  ShieldCheck,
  CalendarRange,
  Search,
} from "lucide-react";
import { formatDisplayTime } from "../services/timezoneService";

const formatPrice = (val, maxDec = null) => {
  if (val === null || val === undefined || val === "") return "-";
  const num = Number(val);
  if (isNaN(num)) return String(val);
  if (maxDec === null) {
    maxDec = num < 5.0 ? 5 : (num < 500.0 ? 3 : 2);
  }
  const str = String(val);
  if (str.includes(".") && str.split(".")[1].length > maxDec) {
    return Number(num.toFixed(maxDec));
  }
  return val;
};

export default function TradeHistoryModal({
  isOpen,
  onClose,
  signals = [],
  currentSymbol = "XAUUSD",
  currentTimeframe = "M5",
  currentTimezone = "local",
  strategyMode = "hma",
  pipMultiplier = 10,
}) {
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  const [resultFilter, setResultFilter] = useState("all"); // all, win, loss, be, running
  const [actionFilter, setActionFilter] = useState("all"); // all, BUY, SELL
  const [timePreset, setTimePreset] = useState("all"); // today, 3d, 7d, 30d, all, custom
  const [searchQuery, setSearchQuery] = useState("");

  const todayStr = new Date().toISOString().split("T")[0];
  const [startDate, setStartDate] = useState(todayStr);
  const [endDate, setEndDate] = useState(todayStr);

  const setQuickCustomRange = (type) => {
    const now = new Date();
    if (type === "today") {
      const dStr = now.toISOString().split("T")[0];
      setStartDate(dStr);
      setEndDate(dStr);
    } else if (type === "yesterday") {
      const y = new Date(now);
      y.setDate(y.getDate() - 1);
      const yStr = y.toISOString().split("T")[0];
      setStartDate(yStr);
      setEndDate(yStr);
    } else if (type === "this_week") {
      const day = now.getDay();
      const diff = now.getDate() - day + (day === 0 ? -6 : 1);
      const monday = new Date(new Date().setDate(diff));
      setStartDate(monday.toISOString().split("T")[0]);
      setEndDate(todayStr);
    } else if (type === "this_month") {
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      setStartDate(firstDay.toISOString().split("T")[0]);
      setEndDate(todayStr);
    }
  };

  const strategyTitle =
    strategyMode === "orb_session"
      ? "ORB Session Pro (Opening Range Breakout)"
      : strategyMode === "bob_volman"
      ? "Bob Volman Pro (Price Action Scalping)"
      : strategyMode === "smc"
      ? "SMC PRO (MSB Dynamic)"
      : strategyMode === "hma"
      ? "HMA Breakout Pro"
      : strategyMode === "liquidity_sweep_pro" || strategyMode === "sweep"
      ? "Hệ Thống Liquidity Sweep & MSS Pro"
      : "Matrix Strategy Pro";

  // Filter signals chronologically descending (newest first)
  const filteredSignals = useMemo(() => {
    let list = signals.slice().reverse();

    // 1. Result filter
    if (resultFilter === "win") {
      list = list.filter((s) => {
        const pips = s.pnl_pips !== undefined ? s.pnl_pips : (s.pnlPips !== undefined ? s.pnlPips : (s.pnl_diff !== undefined ? Math.round(s.pnl_diff * pipMultiplier * 10) / 10 : 0.0));
        const isCanceled = Boolean(s.canceled) || (typeof s.status === "string" && (s.status.startsWith("CANCELED") || s.status === "EXPIRED")) || (s.status === "TIMEOUT" && !s.exit_price);
        const isBE = !isCanceled && (s.status === "BREAKEVEN" || s.status === "BE" || (s.status === "BE_WIN" && pips <= 0) || s.status === "BE_OPPOSITE" || (Math.abs(pips) <= 0.1 && s.exit_price));
        return !isBE && !isCanceled && (s.status === "WIN_TP" || s.status === "WIN_TP1" || s.status === "WIN_TP2" || s.status === "WIN_OPPOSITE" || (s.status === "BE_WIN" && pips > 0) || s.win === true || pips > 0);
      });
    } else if (resultFilter === "loss") {
      list = list.filter((s) => {
        const pips = s.pnl_pips !== undefined ? s.pnl_pips : (s.pnlPips !== undefined ? s.pnlPips : (s.pnl_diff !== undefined ? Math.round(s.pnl_diff * pipMultiplier * 10) / 10 : 0.0));
        const isCanceled = Boolean(s.canceled) || (typeof s.status === "string" && (s.status.startsWith("CANCELED") || s.status === "EXPIRED")) || (s.status === "TIMEOUT" && !s.exit_price);
        const isBE = !isCanceled && (s.status === "BREAKEVEN" || s.status === "BE" || (s.status === "BE_WIN" && pips <= 0) || s.status === "BE_OPPOSITE" || (Math.abs(pips) <= 0.1 && s.exit_price));
        return !isBE && !isCanceled && (s.status === "LOSS_SL" || s.status === "LOSS_OPPOSITE" || s.win === false || pips < 0);
      });
    } else if (resultFilter === "be") {
      list = list.filter((s) => {
        const pips = s.pnl_pips !== undefined ? s.pnl_pips : (s.pnlPips !== undefined ? s.pnlPips : (s.pnl_diff !== undefined ? Math.round(s.pnl_diff * pipMultiplier * 10) / 10 : 0.0));
        const isCanceled = Boolean(s.canceled) || (typeof s.status === "string" && (s.status.startsWith("CANCELED") || s.status === "EXPIRED")) || (s.status === "TIMEOUT" && !s.exit_price);
        const isRunning = s.status === "RUNNING" || s.status === "PENDING" || s.status === "FILLED" || s.status === "OPEN" || s.status === "PENDING_LIMIT";
        return !isCanceled && !isRunning && (s.status === "BREAKEVEN" || s.status === "BE" || s.status === "BE_OPPOSITE" || (s.status === "BE_WIN" && pips <= 0) || (Math.abs(pips) <= 0.1 && s.exit_price));
      });
    } else if (resultFilter === "running") {
      list = list.filter((s) => s.status === "RUNNING" || s.status === "PENDING" || s.status === "FILLED" || s.status === "OPEN" || s.status === "PENDING_LIMIT");
    }

    // 2. Action filter
    if (actionFilter !== "all") {
      list = list.filter((s) => s.action === actionFilter);
    }

    // 3. Time filter
    if (timePreset !== "all") {
      const now = Date.now() / 1000;
      if (timePreset === "today" || timePreset === "24h") {
        const startOfToday = new Date();
        startOfToday.setHours(0, 0, 0, 0);
        const startTs = startOfToday.getTime() / 1000;
        list = list.filter((s) => s.time >= startTs);
      } else if (timePreset === "3d") {
        const threeDaysAgo = now - 86400 * 3;
        list = list.filter((s) => s.time >= threeDaysAgo);
      } else if (timePreset === "7d") {
        const sevenDaysAgo = now - 86400 * 7;
        list = list.filter((s) => s.time >= sevenDaysAgo);
      } else if (timePreset === "30d") {
        const thirtyDaysAgo = now - 86400 * 30;
        list = list.filter((s) => s.time >= thirtyDaysAgo);
      } else if (timePreset === "custom") {
        const startTs = new Date(`${startDate}T00:00:00`).getTime() / 1000;
        const endTs = new Date(`${endDate}T23:59:59`).getTime() / 1000;
        list = list.filter((s) => s.time >= startTs && s.time <= endTs);
      }
    }

    // 4. Search query
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (s) =>
          String(s.price).includes(q) ||
          String(s.sl).includes(q) ||
          String(s.tp1).includes(q) ||
          s.action?.toLowerCase().includes(q) ||
          s.status?.toLowerCase().includes(q)
      );
    }

    return list;
  }, [signals, resultFilter, actionFilter, timePreset, startDate, endDate, searchQuery]);

  // Overall Statistics from filtered list
  const stats = useMemo(() => {
    let wins = 0;
    let losses = 0;
    let be = 0;
    let canceled = 0;
    let running = 0;
    let totalPnlPips = 0;
    let grossProfit = 0;
    let grossLoss = 0;

    filteredSignals.forEach((s) => {
      const pips = s.pnl_pips !== undefined ? s.pnl_pips : (s.pnlPips !== undefined ? s.pnlPips : (s.pnl_diff !== undefined ? Math.round(s.pnl_diff * pipMultiplier * 10) / 10 : 0.0));
      const diff = s.pnl_diff !== undefined ? s.pnl_diff : (pips / pipMultiplier);
      const isRunning = s.status === "RUNNING" || s.status === "PENDING" || s.status === "FILLED" || s.status === "OPEN" || s.status === "PENDING_LIMIT";
      const isCanceled = Boolean(s.canceled) || (typeof s.status === "string" && (s.status.startsWith("CANCELED") || s.status === "EXPIRED")) || (s.status === "TIMEOUT" && !s.exit_price);
      const isSessionClose = s.status === "SESSION_CLOSE" || (s.status === "TIMEOUT" && (pips !== 0 || s.exit_price));
      const isBE = !isCanceled && (s.status === "BREAKEVEN" || s.status === "BE" || (s.status === "BE_WIN" && pips <= 0) || s.status === "BE_OPPOSITE" || (Math.abs(pips) <= 0.1 && s.exit_price && !isRunning && !isSessionClose));

      if (isRunning) {
        running++;
      } else if (isCanceled) {
        canceled++;
      } else if (isBE) {
        be++;
        totalPnlPips += pips;
      } else if (s.status === "WIN_TP" || s.status === "WIN_TP1" || s.status === "WIN_TP2" || s.status === "WIN_OPPOSITE" || (s.status === "BE_WIN" && pips > 0) || s.win === true || pips > 0) {
        wins++;
        grossProfit += Math.abs(pips);
        totalPnlPips += pips;
      } else if (s.status === "LOSS_SL" || s.status === "LOSS_OPPOSITE" || s.win === false || pips < 0) {
        losses++;
        grossLoss += Math.abs(pips);
        totalPnlPips += pips;
      } else {
        be++;
        totalPnlPips += pips;
      }
    });

    const totalClosed = wins + losses + be;
    const winRate = (wins + losses) > 0 ? Math.round((wins / (wins + losses)) * 1000) / 10 : 0;
    const profitFactor = grossLoss > 0 ? (grossProfit / grossLoss).toFixed(2) : grossProfit > 0 ? "99.0" : "1.00";

    return {
      total: filteredSignals.length,
      totalClosed,
      wins,
      losses,
      be,
      canceled,
      running,
      winRate,
      totalPnlPips: Math.round(totalPnlPips * 10) / 10,
      profitFactor,
    };
  }, [filteredSignals, pipMultiplier]);

  if (!isOpen || typeof document === "undefined") return null;

  return createPortal(
    <div
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      className="fixed inset-0 z-[9999] bg-slate-950/85 backdrop-blur-md flex items-center justify-center p-3 sm:p-6 animate-fadeIn"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-slate-900 border border-slate-800 rounded-2xl max-w-6xl w-full max-h-[92vh] flex flex-col shadow-2xl overflow-hidden"
      >
        {/* 1. Modal Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800 bg-slate-950/60">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-400 shadow-sm">
              <History className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-extrabold text-sm sm:text-base text-slate-100 uppercase tracking-wide">
                  Nhật Ký Chi Tiết Toàn Bộ Lệnh
                </h3>
                <span className="px-2 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30 text-[10px] font-bold font-mono">
                  PRO TRADER
                </span>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-400 mt-0.5 font-mono">
                <span className="text-cyan-400 font-bold">{currentSymbol}</span>
                <span>•</span>
                <span className="text-slate-300 font-semibold">{currentTimeframe}</span>
                <span>•</span>
                <span className="text-purple-300">{strategyTitle}</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2.5">
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg bg-slate-800/80 hover:bg-slate-700 text-slate-400 hover:text-white flex items-center justify-center transition cursor-pointer"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* 2. Top Summary KPI Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5 p-4 bg-slate-950/40 border-b border-slate-800/80 text-xs font-mono">
          <div className="p-2.5 rounded-xl bg-slate-950/80 border border-slate-800">
            <span className="text-[10px] text-slate-400 font-sans block">TỔNG SỐ LỆNH</span>
            <span className="font-extrabold text-slate-100 text-base">{stats.total}</span>
          </div>

          <div className="p-2.5 rounded-xl bg-slate-950/80 border border-slate-800">
            <span className="text-[10px] text-emerald-400 font-sans block">TỶ LỆ THẮNG (WIN RATE)</span>
            <span className="font-extrabold text-emerald-400 text-base">{stats.winRate}%</span>
          </div>

          <div className="p-2.5 rounded-xl bg-slate-950/80 border border-slate-800">
            <span className="text-[10px] text-slate-400 font-sans block">THẮNG / THUA / HÒA</span>
            <div className="flex items-center gap-1 font-extrabold text-sm">
              <span className="text-emerald-400">{stats.wins}W</span>
              <span className="text-slate-500">/</span>
              <span className="text-rose-400">{stats.losses}L</span>
              <span className="text-slate-500">/</span>
              <span className="text-amber-300">{stats.be}BE</span>
            </div>
          </div>

          <div className="p-2.5 rounded-xl bg-slate-950/80 border border-slate-800">
            <span className="text-[10px] text-amber-400 font-sans block">PROFIT FACTOR</span>
            <span className="font-extrabold text-amber-400 text-base">{stats.profitFactor}</span>
          </div>

          <div className="p-2.5 rounded-xl bg-slate-950/80 border border-slate-800">
            <span className="text-[10px] text-purple-300 font-sans block">LỢI NHUẬN RÒNG</span>
            <span className={`font-extrabold text-base ${stats.totalPnlPips >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
              {stats.totalPnlPips >= 0 ? `+${stats.totalPnlPips}` : stats.totalPnlPips} pips
            </span>
          </div>

          <div className="p-2.5 rounded-xl bg-slate-950/80 border border-slate-800">
            <span className="text-[10px] text-cyan-400 font-sans block">ĐANG CHẠY / PENDING</span>
            <span className="font-extrabold text-cyan-300 text-base">{stats.running} lệnh</span>
          </div>
        </div>

        {/* 3. Filter & Controls Toolbar */}
        <div className="p-4 bg-slate-900/90 border-b border-slate-800/80 flex flex-wrap items-center justify-between gap-3 text-xs">
          {/* Result Filter Tabs */}
          <div className="flex flex-wrap items-center gap-1 bg-slate-950 p-1 rounded-xl border border-slate-800">
            <button
              onClick={() => setResultFilter("all")}
              className={`px-2.5 py-1 rounded-lg font-semibold transition cursor-pointer ${
                resultFilter === "all" ? "bg-slate-800 text-white shadow" : "text-slate-400 hover:text-slate-200"
              }`}
            >
              Tất Cả ({signals.length})
            </button>
            <button
              onClick={() => setResultFilter("win")}
              className={`px-2.5 py-1 rounded-lg font-semibold transition cursor-pointer ${
                resultFilter === "win" ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/40" : "text-slate-400 hover:text-emerald-300"
              }`}
            >
              Thắng
            </button>
            <button
              onClick={() => setResultFilter("loss")}
              className={`px-2.5 py-1 rounded-lg font-semibold transition cursor-pointer ${
                resultFilter === "loss" ? "bg-rose-500/20 text-rose-400 border border-rose-500/40" : "text-slate-400 hover:text-rose-300"
              }`}
            >
              Thua
            </button>
            <button
              onClick={() => setResultFilter("be")}
              className={`px-2.5 py-1 rounded-lg font-semibold transition cursor-pointer ${
                resultFilter === "be" ? "bg-amber-500/20 text-amber-300 border border-amber-500/40" : "text-slate-400 hover:text-amber-300"
              }`}
            >
              Hòa Vốn
            </button>
            <button
              onClick={() => setResultFilter("running")}
              className={`px-2.5 py-1 rounded-lg font-semibold transition cursor-pointer ${
                resultFilter === "running" ? "bg-cyan-500/20 text-cyan-300 border border-cyan-500/40" : "text-slate-400 hover:text-cyan-300"
              }`}
            >
              Đang Chạy
            </button>
          </div>

          {/* Time Preset Filter */}
          <div className="flex items-center gap-1 bg-slate-950 p-1 rounded-xl border border-slate-800 font-mono text-[11px]">
            {["all", "today", "3d", "7d", "30d"].map((t) => (
              <button
                key={t}
                onClick={() => setTimePreset(t)}
                className={`px-2 py-0.5 rounded transition cursor-pointer uppercase ${
                  timePreset === t ? "bg-cyan-500 text-slate-950 font-bold" : "text-slate-400 hover:text-slate-200"
                }`}
              >
                {t === "all" ? "Tất Cả" : t === "today" ? "Hôm Nay" : t}
              </button>
            ))}

            <button
              onClick={() => setTimePreset(timePreset === "custom" ? "all" : "custom")}
              className={`px-2 py-0.5 rounded flex items-center gap-1 transition cursor-pointer font-sans text-xs ${
                timePreset === "custom"
                  ? "bg-amber-500 text-slate-950 font-bold shadow-sm"
                  : "text-amber-400 hover:bg-amber-500/10 border border-amber-500/30"
              }`}
              title="Tùy chỉnh chọn khoảng ngày bắt đầu và kết thúc"
            >
              <CalendarRange className="w-3 h-3" />
              <span>📅 Tùy chỉnh ngày</span>
            </button>
          </div>

          {/* Search Box */}
          <div className="relative min-w-[180px]">
            <Search className="w-3.5 h-3.5 text-slate-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Tìm mức giá, mã..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-slate-950 border border-slate-800 rounded-lg pl-8 pr-2.5 py-1 text-slate-200 text-xs focus:outline-none focus:border-cyan-500"
            />
          </div>
        </div>

        {/* Custom Date Range Picker Strip (when custom is active) */}
        {timePreset === "custom" && (
          <div className="px-5 py-2.5 bg-slate-950/90 border-b border-amber-500/30 flex flex-wrap items-center justify-between gap-3 text-xs animate-fadeIn">
            <div className="flex flex-wrap items-center gap-3">
              <span className="font-bold text-amber-400 flex items-center gap-1.5">
                <CalendarRange className="w-3.5 h-3.5" />
                <span>Lọc Theo Khoảng Ngày:</span>
              </span>

              <div className="flex items-center gap-1.5">
                <span className="text-slate-400 text-[11px]">Từ ngày:</span>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="bg-slate-900 border border-slate-700 rounded px-2.5 py-1 text-slate-200 text-xs font-mono focus:outline-none focus:border-amber-400"
                />
              </div>

              <div className="flex items-center gap-1.5">
                <span className="text-slate-400 text-[11px]">Đến ngày:</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="bg-slate-900 border border-slate-700 rounded px-2.5 py-1 text-slate-200 text-xs font-mono focus:outline-none focus:border-amber-400"
                />
              </div>
            </div>

            <div className="flex items-center gap-1 font-sans text-[11px]">
              <span className="text-slate-500 mr-1">Chọn nhanh:</span>
              <button
                onClick={() => setQuickCustomRange("today")}
                className="px-2 py-0.5 rounded bg-slate-900 hover:bg-slate-800 border border-slate-700 text-slate-300 transition cursor-pointer"
              >
                Hôm nay
              </button>
              <button
                onClick={() => setQuickCustomRange("yesterday")}
                className="px-2 py-0.5 rounded bg-slate-900 hover:bg-slate-800 border border-slate-700 text-slate-300 transition cursor-pointer"
              >
                Hôm qua
              </button>
              <button
                onClick={() => setQuickCustomRange("this_week")}
                className="px-2 py-0.5 rounded bg-slate-900 hover:bg-slate-800 border border-slate-700 text-slate-300 transition cursor-pointer"
              >
                Tuần này
              </button>
              <button
                onClick={() => setQuickCustomRange("this_month")}
                className="px-2 py-0.5 rounded bg-slate-900 hover:bg-slate-800 border border-slate-700 text-slate-300 transition cursor-pointer"
              >
                Tháng này
              </button>
            </div>
          </div>
        )}

        {/* 4. Table Area */}
        <div className="flex-1 overflow-auto max-h-[55vh]">
          {filteredSignals.length === 0 ? (
            <div className="p-12 text-center text-slate-500 text-xs">
              <Clock className="w-8 h-8 mx-auto mb-2 text-slate-600" />
              <p>Không tìm thấy lệnh nào phù hợp với bộ lọc hiện tại.</p>
            </div>
          ) : (
            <table className="w-full text-left border-collapse text-xs font-mono">
              <thead className="sticky top-0 z-10 bg-slate-950 border-b border-slate-800 text-[10px] text-slate-400 uppercase tracking-wider">
                <tr>
                  <th className="py-2.5 px-3">#</th>
                  <th className="py-2.5 px-3">Thời Gian Vào</th>
                  <th className="py-2.5 px-3">Lệnh</th>
                  <th className="py-2.5 px-3">Giá Vào</th>
                  <th className="py-2.5 px-3">Cắt Lỗ (SL)</th>
                  <th className="py-2.5 px-3">Chốt Lời (TP)</th>
                  <th className="py-2.5 px-3">Tỷ Lệ R:R</th>
                  <th className="py-2.5 px-3 text-right">Kết Quả (Pips)</th>
                  <th className="py-2.5 px-3 text-center">Trạng Thái</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/50">
                {filteredSignals.map((sig, idx) => {
                  const pips = sig.pnl_pips !== undefined ? sig.pnl_pips : (sig.pnlPips !== undefined ? sig.pnlPips : (sig.pnl_diff !== undefined ? Math.round(sig.pnl_diff * pipMultiplier * 10) / 10 : 0.0));
                  const diff = sig.pnl_diff !== undefined ? sig.pnl_diff : (pips / pipMultiplier);
                  const isRunning = sig.status === "RUNNING" || sig.status === "PENDING" || sig.status === "FILLED" || sig.status === "OPEN" || sig.status === "PENDING_LIMIT";
                  const isCanceled = Boolean(sig.canceled) || (typeof sig.status === "string" && (sig.status.startsWith("CANCELED") || sig.status === "EXPIRED")) || (sig.status === "TIMEOUT" && !sig.exit_price);
                  const isSessionClose = sig.status === "SESSION_CLOSE" || (sig.status === "TIMEOUT" && (pips !== 0 || sig.exit_price));
                  const isBE = !isCanceled && (sig.status === "BREAKEVEN" || sig.status === "BE" || (sig.status === "BE_WIN" && pips <= 0) || sig.status === "BE_OPPOSITE" || (Math.abs(pips) <= 0.1 && sig.exit_price && !isRunning && !isSessionClose));
                  const isWin = !isBE && !isCanceled && (sig.status === "WIN_TP" || sig.status === "WIN_TP1" || sig.status === "WIN_TP2" || sig.status === "WIN_OPPOSITE" || (sig.status === "BE_WIN" && pips > 0) || sig.win === true || pips > 0);

                  return (
                    <tr key={sig.id || sig.order_id || idx} className="hover:bg-slate-800/40 transition">
                      <td className="py-2 px-3 text-slate-500 text-[11px]">{idx + 1}</td>
                      <td className="py-2 px-3 text-slate-300 whitespace-nowrap">
                        <span className="text-amber-300 font-semibold">
                          {formatDisplayTime(sig.time, currentTimezone, "time")}
                        </span>{" "}
                        <span className="text-slate-500 text-[10px]">
                          ({formatDisplayTime(sig.time, currentTimezone, "date")})
                        </span>
                      </td>
                      <td className="py-2 px-3">
                        <div className="flex items-center gap-1.5">
                          <span
                            className={`px-2 py-0.5 rounded font-extrabold text-[10px] inline-flex items-center gap-1 ${
                              sig.action === "BUY"
                                ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30"
                                : "bg-rose-500/20 text-rose-400 border border-rose-500/30"
                            }`}
                          >
                            {sig.action === "BUY" ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                            {sig.action}
                          </span>
                          {sig.setup_type && (
                            <span className="px-1.5 py-0.2 rounded font-mono font-bold text-[9.5px] bg-cyan-500/15 text-cyan-300 border border-cyan-500/30">
                              {sig.setup_type}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-2 px-3 font-bold text-slate-200">{formatPrice(sig.price || sig.entry)}</td>
                      <td className="py-2 px-3 text-rose-400">{formatPrice(sig.sl)}</td>
                      <td className="py-2 px-3 text-emerald-400">{formatPrice(sig.tp1 || sig.tp)}</td>
                      <td className="py-2 px-3 text-cyan-300 font-bold">{sig.rr_ratio || "1:1.0"}</td>
                      <td className="py-2 px-3 text-right whitespace-nowrap">
                        {isCanceled ? (
                          <span className="text-slate-500">--</span>
                        ) : isRunning ? (
                          <span className="text-cyan-400 font-bold animate-pulse">Running</span>
                        ) : isBE ? (
                          <span className="text-amber-300 font-bold">0.0 p</span>
                        ) : (
                          <span
                            className={`font-black text-[11px] ${
                              isWin ? "text-emerald-400" : "text-rose-400"
                            }`}
                          >
                            {pips >= 0 ? `+${pips}` : `${pips}`} p
                          </span>
                        )}
                      </td>
                      <td className="py-2 px-3 text-center">
                        <span
                          className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                            isCanceled
                              ? "bg-slate-800 text-slate-400 border border-slate-700"
                              : isSessionClose
                              ? (pips >= 0 ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40" : "bg-rose-500/20 text-rose-300 border border-rose-500/40")
                              : isRunning
                              ? "bg-cyan-500/20 text-cyan-400 border border-cyan-500/40"
                              : (sig.status === "BE_WIN" && pips > 0)
                              ? "bg-teal-500/20 text-teal-300 border border-teal-500/40"
                              : isBE
                              ? "bg-amber-500/20 text-amber-300 border border-amber-500/40"
                              : isWin
                              ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/40"
                              : "bg-rose-500/20 text-rose-400 border border-rose-500/40"
                          }`}
                        >
                          {isCanceled
                            ? (sig.status === "CANCELED_TP_HIT" ? "HỦY (ĐẠT TP TRƯỚC)" : sig.status === "EXPIRED" ? "HẾT HẠN (LIMIT)" : "HỦY (TIMEOUT)")
                            : isSessionClose
                            ? `HẾT PHIÊN (${pips >= 0 ? `+${pips}` : pips}p)`
                            : isRunning
                            ? "ĐANG CHẠY"
                            : (sig.status === "BE_WIN" && pips > 0)
                            ? `HÒA VỐN (+${pips}p)`
                            : isBE
                            ? "HÒA VỐN"
                            : isWin
                            ? "WIN (TP HIT)"
                            : "LOSS (SL HIT)"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* 5. Modal Footer */}
        <div className="px-5 py-3 border-t border-slate-800 bg-slate-950/70 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-400">
          <div className="flex items-center gap-2">
            <span>Hiển thị <strong>{filteredSignals.length}</strong> / {signals.length} lệnh</span>
            <span>•</span>
            <span className="text-slate-500">Múi giờ: {currentTimezone}</span>
          </div>

          <button
            onClick={onClose}
            className="px-4 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold transition cursor-pointer"
          >
            Đóng
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
